import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Receipt, Printer } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PAYMENT_METHODS = ["Cash", "Debit", "Credit Card", "E-transfer", "Other"];

function fmt(n) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(n || 0);
}

export function SalesList() {
  const [sales, setSales] = useState([]);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => { api.get("/sales").then(({ data }) => setSales(data)); }, []);

  const viewReceipt = async (id) => {
    const { data } = await api.get(`/sales/${id}`);
    setReceipt(data);
  };

  return (
    <div className="space-y-6" data-testid="sales-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy">Sales</h1>
          <p className="text-sm text-muted-foreground">Latest transactions across the store.</p>
        </div>
        <Link to="/sales/new">
          <Button data-testid="new-sale-btn" className="bg-gold hover:bg-gold-hover text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Sale
          </Button>
        </Link>
      </div>

      <Card className="section-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No sales yet.</td></tr>
              ) : sales.map((s) => (
                <tr key={s.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`sale-row-${s.id}`}>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-navy">{s.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {s.items.map(i => `${i.product_name} x${i.quantity}`).join(", ")}
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{s.payment_method}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold text-navy">{fmt(s.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`view-sale-${s.id}`} onClick={() => viewReceipt(s.id)}>
                      <Receipt className="w-3 h-3 mr-1" /> Receipt
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          {receipt && (
            <div id="printable-receipt" className="font-mono text-sm bg-white p-4 border border-border/60 rounded">
              <div className="text-center mb-3">
                <div className="font-heading text-lg font-bold text-navy">Top Media Corp.</div>
                <div className="text-xs text-muted-foreground">Entertainment Company</div>
              </div>
              <div className="text-xs text-muted-foreground border-b border-dashed pb-2 mb-2">
                <div>{new Date(receipt.created_at).toLocaleString()}</div>
                <div>Customer: {receipt.customer_name}</div>
                <div>Receipt #: {receipt.id.slice(0, 8).toUpperCase()}</div>
              </div>
              <div className="space-y-1">
                {receipt.items.map((it, idx) => (
                  <div key={`${it.product_id}-${idx}`} className="flex justify-between">
                    <div className="truncate pr-2">{it.product_name} × {it.quantity}</div>
                    <div>${(it.line_total).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed mt-3 pt-2 space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>${receipt.subtotal.toFixed(2)}</span></div>
                {receipt.discount > 0 && <div className="flex justify-between text-muted-foreground"><span>Discount</span><span>-${receipt.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-navy text-base"><span>TOTAL</span><span>${receipt.total.toFixed(2)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Payment</span><span>{receipt.payment_method}</span></div>
              </div>
              {receipt.notes && <div className="mt-3 text-xs text-muted-foreground">Notes: {receipt.notes}</div>}
              <div className="text-center text-xs text-muted-foreground mt-4">Thank you for your business!</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => window.print()} data-testid="print-receipt-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SaleNew() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [newCust, setNewCust] = useState(null);
  const [items, setItems] = useState([{ key: "line-1", product_id: "", quantity: 1, unit_price: 0 }]);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedReceipt, setSavedReceipt] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, []);

  const onSelectProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    setItems((arr) => arr.map((it, i) =>
      i === idx ? { ...it, product_id: pid, unit_price: p ? p.selling_price : 0 } : it
    ));
  };

  const setItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addLine = () =>
    setItems((arr) => [
      ...arr,
      { key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, product_id: "", quantity: 1, unit_price: 0 },
    ]);
  const removeLine = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0),
    [items],
  );
  const total = Math.max(0, subtotal - (Number(discount) || 0));

  const onSubmit = async (e) => {
    e.preventDefault();
    const valid = items.every((it) => it.product_id && Number(it.quantity) > 0);
    if (!valid) { toast.error("Please complete all line items"); return; }
    if (!customerId && !newCust) { toast.error("Select or create a customer"); return; }
    setSubmitting(true);
    try {
      const payload = {
        customer_id: customerId || null,
        new_customer: newCust,
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
        })),
        payment_method: paymentMethod,
        discount: Number(discount) || 0,
        notes,
      };
      const { data } = await api.post("/sales", payload);
      toast.success("Sale saved");
      setSavedReceipt(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="new-sale-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-navy">New Sale</h1>
        <p className="text-sm text-muted-foreground">Create a sale and auto-update inventory.</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        <Card className="section-card space-y-4">
          <h3 className="font-heading text-lg font-semibold text-navy">Customer</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Existing customer</Label>
              <Select value={customerId} onValueChange={(v) => { setCustomerId(v); setNewCust(null); }}>
                <SelectTrigger data-testid="sale-customer-select"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" data-testid="add-walkin-btn"
                onClick={() => { setCustomerId(""); setNewCust(newCust ? null : { name: "", phone: "", email: "", subscription_plan: "", server_used: "" }); }}>
                {newCust ? "Cancel new customer" : "+ Walk-in / New customer"}
              </Button>
            </div>
          </div>
          {newCust && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-border/60">
              <Input data-testid="newcust-name" placeholder="Name *" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} />
              <Input data-testid="newcust-phone" placeholder="Phone *" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} />
              <Input data-testid="newcust-email" placeholder="Email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} />
              <Input data-testid="newcust-plan" placeholder="Subscription plan" value={newCust.subscription_plan} onChange={(e) => setNewCust({ ...newCust, subscription_plan: e.target.value })} />
            </div>
          )}
        </Card>

        <Card className="section-card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-navy">Items</h3>
            <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
              <Plus className="w-3 h-3 mr-1" /> Add line
            </Button>
          </div>
          <div className="space-y-3">
            {items.map((it, idx) => {
              const p = products.find((x) => x.id === it.product_id);
              return (
                <div key={it.key} className="grid grid-cols-12 gap-2 items-end" data-testid={`sale-item-${idx}`}>
                  <div className="col-span-12 md:col-span-6 space-y-1.5">
                    <Label className="text-xs">Product</Label>
                    <Select value={it.product_id} onValueChange={(v) => onSelectProduct(idx, v)}>
                      <SelectTrigger data-testid={`item-product-${idx}`}><SelectValue placeholder="Select product..." /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {products.map((pp) => (
                          <SelectItem key={pp.id} value={pp.id} disabled={pp.quantity === 0}>
                            {pp.name} {pp.quantity === 0 ? "(out)" : `· ${pp.quantity} in stock`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4 md:col-span-2 space-y-1.5">
                    <Label className="text-xs">Qty</Label>
                    <Input type="number" min="1" max={p?.quantity || undefined}
                      data-testid={`item-qty-${idx}`} value={it.quantity}
                      onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                  </div>
                  <div className="col-span-6 md:col-span-3 space-y-1.5">
                    <Label className="text-xs">Unit price</Label>
                    <Input type="number" step="0.01" data-testid={`item-price-${idx}`} value={it.unit_price}
                      onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)} disabled={items.length === 1}
                      data-testid={`remove-item-${idx}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="section-card space-y-4 lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold text-navy">Payment & Notes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Payment method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="sale-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Discount (CAD)</Label>
                <Input type="number" step="0.01" min="0" data-testid="sale-discount-input"
                  value={discount} onChange={(e) => setDiscount(e.target.value)} />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Textarea data-testid="sale-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              </div>
            </div>
          </Card>

          <Card className="section-card space-y-3 lg:col-span-1">
            <h3 className="font-heading text-lg font-semibold text-navy">Summary</h3>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span data-testid="sale-subtotal">{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Discount</span><span>-{fmt(discount)}</span></div>
            <div className="flex justify-between font-heading text-2xl font-semibold text-navy border-t border-border/60 pt-3">
              <span>Total</span><span data-testid="sale-total">{fmt(total)}</span>
            </div>
            <Button type="submit" disabled={submitting} data-testid="save-sale-btn"
              className="w-full bg-navy hover:bg-navy-hover text-white">
              {submitting ? "Saving..." : "Save sale"}
            </Button>
          </Card>
        </div>
      </form>

      <Dialog open={!!savedReceipt} onOpenChange={(o) => { if (!o) { setSavedReceipt(null); navigate("/sales"); } }}>
        <DialogContent className="max-w-md" data-testid="saved-receipt-dialog">
          <DialogHeader><DialogTitle>Sale completed</DialogTitle></DialogHeader>
          {savedReceipt && (
            <div className="font-mono text-sm bg-white p-4 border border-border/60 rounded">
              <div className="text-center mb-3">
                <div className="font-heading text-lg font-bold text-navy">Top Media Corp.</div>
                <div className="text-xs text-muted-foreground">Receipt #{savedReceipt.id.slice(0, 8).toUpperCase()}</div>
              </div>
              <div className="space-y-1">
                {savedReceipt.items.map((it, idx) => (
                  <div key={`${it.product_id}-${idx}`} className="flex justify-between">
                    <div>{it.product_name} × {it.quantity}</div>
                    <div>${it.line_total.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed mt-3 pt-2 space-y-1">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>${savedReceipt.subtotal.toFixed(2)}</span></div>
                {savedReceipt.discount > 0 && <div className="flex justify-between text-muted-foreground"><span>Discount</span><span>-${savedReceipt.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-navy"><span>TOTAL</span><span>${savedReceipt.total.toFixed(2)}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate("/sales")} data-testid="done-receipt-btn">Done</Button>
            <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
